import {safeContactDiagnostic,safeExclusions} from './contactReadDiagnostics.js'
import {CONTACT_REVIEW_V2,contactReviewMetadata,reviewContactFields} from './contactReviewContract.js'
/** Per-view read queue, not an authorization cache. Only observed IDs may enter. */
export function contactReadResult(value) {
  const diagnostic = safeContactDiagnostic(value?.diagnostics)
  const meta = diagnostic ? {diagnostics:diagnostic} : {}
  const unavailable = {state:'unavailable',contacts:[],...meta}
  if(value?.readContract!==undefined){
    if(value.readContract!==CONTACT_REVIEW_V2)return unavailable
    const review=contactReviewMetadata(value)
    if(!review||!Array.isArray(value.contacts))return unavailable
    if(['restricted','unavailable'].includes(value.state))return value.complete===false&&!value.contacts.length?{...unavailable,...review,state:value.state,complete:false}:unavailable
    if(!['available','partial','empty','review_required'].includes(value.state)||value.state!=='partial'&&value.complete!==true||['empty','review_required'].includes(value.state)&&value.contacts.length||value.state==='available'&&!value.contacts.length)return unavailable
    if(value.state==='empty'&&value.reason!=='no_linked_contacts'||value.state==='review_required'&&value.reason!=='no_matching_linked_contacts')return unavailable
    const contacts=[],ids=new Set(review.entryDiagnostics.map(entry=>entry.contactId))
    for(const row of value.contacts){
      if(ids.has(row?.id)||row?.reviewRequired!==true||row.autoRecipientEligible!==false)return unavailable
      const single=contactReadResult({state:'available',complete:true,contacts:[row]})
      if(single.state!=='available')return unavailable
      const safe=reviewContactFields({...single.contacts[0],reviewRequired:true,autoRecipientEligible:false});if(!safe)return unavailable
      ids.add(row.id);contacts.push(safe)
    }
    return {...review,...meta,state:value.state,complete:value.complete===true,contacts,reason:value.reason==='contact_sources_partial'?'contact_sources_partial':value.state==='partial'?'contact_entries_partial':value.state==='empty'?'no_linked_contacts':value.state==='review_required'?'no_matching_linked_contacts':''}
  }
  if (!value || !['available','empty','review_required','restricted','unavailable'].includes(value.state)) return unavailable
  if (['restricted','unavailable'].includes(value.state)) return value.complete === false && Array.isArray(value.contacts) && value.contacts.length === 0 ? {state:value.state,contacts:[],...meta} : unavailable
  if (value.complete !== true || !Array.isArray(value.contacts)) return unavailable
  if (value.state === 'review_required') return value.contacts.length === 0 && value.reason === 'no_matching_linked_contacts' ? {state:'review_required',contacts:[],...meta} : unavailable
  if (value.state === 'empty') return value.contacts.length === 0 && value.reason === 'no_linked_contacts' ? {state:'empty',contacts:[],...meta} : unavailable
  if (!value.contacts.length || value.contacts.some(c => !c || typeof c.id !== 'string' || !c.id ||
    ['firstName','lastName','email'].some(key => typeof c[key] !== 'string') ||
    (c.phone!==undefined&&(typeof c.phone!=='string'||c.phone.length>100||/[\r\n\0]/.test(c.phone))) ||
    c.provenance?.association !== 'exact_championship' || !['exact','canonical_team','administrative_role'].includes(c.provenance.sportMatch) || typeof c.provenance.role !== 'string' || typeof c.provenance.sport !== 'string') ||
    new Set(value.contacts.map(c=>c.id)).size !== value.contacts.length) return unavailable
  return {state:'available',...meta,...(safeExclusions(value.exclusions) ? {exclusions:safeExclusions(value.exclusions)} : {}),contacts:value.contacts.map(({id,firstName,lastName,email,phone,provenance})=>({id,firstName,lastName,email,phone:typeof phone==='string'?phone:'',provenance}))}
}
export function createVisibleContactReader({ids,load,onUpdate,schedule=queueMicrotask}) {
  const allowed=new Set(ids.map(String)),seen=new Set(),queue=new Set(),active=new Set()
  let disposed=false,running=false,scheduled=false,controller=null
  const emit=(id,value)=>{if(!disposed)onUpdate(id,value)}
  const pump=async()=>{
    scheduled=false
    if(disposed||running||!queue.size)return
    running=true;const batch=[...queue].slice(0,25);for(const id of batch){queue.delete(id);active.add(id)}
    controller=new AbortController()
    try {
      const response=await load(batch,controller.signal)
      const valid=response?.ok===true&&response.source==='exact_championship_associations'
      for(const id of batch)emit(id,contactReadResult(valid?response.results?.[id]:null))
    }catch(error){const diagnostics=safeContactDiagnostic(error?.body?.diagnostics);for(const id of batch)emit(id,{state:'unavailable',contacts:[],...(diagnostics?{diagnostics}:{})})}
    finally{for(const id of batch)active.delete(id);running=false;controller=null;if(!disposed&&queue.size)start()}
  }
  const start=()=>{if(!disposed&&!scheduled&&!running){scheduled=true;schedule(pump)}}
  return {
    observe(id,{retry=false}={}){id=String(id);if(disposed||!allowed.has(id)||(!retry&&seen.has(id))||queue.has(id))return
      // A pending read cannot be duplicated by repeated retry clicks.
      if(active.has(id))return
      seen.add(id);queue.add(id);emit(id,{state:'loading',contacts:[]});start()
    },
    dispose(){disposed=true;queue.clear();controller?.abort()},
  }
}

/** Global transport serialization only; no shared contact result cache. */
export function createContactLoadQueue(){
 let tail=Promise.resolve()
 return (load,signal)=>{
  const next=tail.then(()=>{if(signal.aborted)throw Object.assign(new Error('Read canceled'),{name:'AbortError'});return load()})
  tail=next.catch(()=>{})
  return next
 }
}

// Both direct reads and single-row school reads share one transport lane.
export const visibleContactReadQueue = createContactLoadQueue()
